// =================================================================
// Store: Session slice (PSD §9.1.7 / TODO Tasks 3.8)
// =================================================================
import type { StateCreator } from 'zustand';
import { v4 as uuid } from 'uuid';
import { PKSource, ToxicityRisk, ConfidenceStatus, type AuditEntry, type SessionRecord, type SafetyAssessment, type MonitoringSchedule, type PKParameters } from '@/types';
import { sessionRepo, auditRepo } from '@/persistence/sessionAuditRepo';
import { computeAuditHash, ENGINE_VERSION } from '@/engine';
import type { StoreState, SessionSlice } from './types';

export const createSessionSlice: StateCreator<StoreState, [], [], SessionSlice> = (set, get) => ({
  currentSessionId: null,
  sessions: [],
  auditEntries: [],

  saveSession: async () => {
    const state = get();
    if (!state.patient || !state.regimen || !state.dosingWeight || !state.renal) return;
    const now = new Date().toISOString();
    const id = state.currentSessionId ?? uuid();
    const session: SessionRecord = {
      id,
      patientId: 'local',
      createdAtISO: now,
      updatedAtISO: now,
      patientInput: state.patient,
      strategyConfig: state.strategyConfig,
      dosingWeight: state.dosingWeight,
      renal: state.renal,
      regimen: state.regimen,
      safety: state.safety ?? emptySafety(),
      monitoring: state.monitoring ?? emptyMonitoring(),
      levels: state.levels,
      pkParameters: state.pkParameters ?? emptyPK(),
    };
    await sessionRepo.saveSession(session);
    set({ currentSessionId: id });
    await get().refreshSessions();
  },

  loadSession: async (id) => {
    const session = await sessionRepo.loadSession(id);
    if (!session) return;
    set({
      currentSessionId: session.id,
      patient: session.patientInput,
      strategyConfig: session.strategyConfig,
      dosingWeight: session.dosingWeight,
      renal: session.renal,
      regimen: session.regimen,
      safety: session.safety,
      monitoring: session.monitoring,
      levels: session.levels,
      pkParameters: session.pkParameters,
    });
  },

  refreshSessions: async () => {
    const sessions = await sessionRepo.listSessions();
    set({ sessions });
  },

  logAuditEvent: async (event) => {
    const entryBase = {
      id: uuid(),
      timestampISO: new Date().toISOString(),
      sessionId: get().currentSessionId ?? 'unattached',
      eventType: event.eventType,
      fieldChanged: event.fieldChanged,
      oldValue: event.oldValue,
      newValue: event.newValue,
      engineVersion: ENGINE_VERSION,
    };
    const prior = get().auditEntries[0]?.hash ?? '';
    const hash = await computeAuditHash(entryBase, prior);
    const entry: AuditEntry = { ...entryBase, hash };
    await auditRepo.appendEntry(entry);
    set((state) => ({ auditEntries: [entry, ...state.auditEntries] }));
  },
});

function emptySafety(): SafetyAssessment {
  return {
    contraindications: [],
    eiBlocked: false,
    eiBlockReasons: [],
    renalGatePassed: true,
    unstableRenalWarning: false,
    implausibleInput: [],
    nonOverridableCaution: false,
    toxicityRisk: ToxicityRisk.Low,
    toxicityRiskFactors: [],
  };
}
function emptyMonitoring(): MonitoringSchedule {
  return {
    samplingSchedule: [],
    monitoringPathway: 'nomogram',
    scrBunFrequencyDays: 3,
    scrBunFrequencyReason: '',
    reassessmentTriggers: [],
  };
}
function emptyPK(): PKParameters {
  return {
    source: PKSource.Population,
    kelPerHr: 0.05,
    halfLifeHr: 14,
    vdLkg: 0.26,
    vdL: 18,
    method: 'cockcroft-gault',
    confidence: ConfidenceStatus.Empiric,
  };
}
